import React, { useState, useEffect, useCallback } from 'react';
import { UsuarioListItem } from '../types/usuario.types';
import { usuarioService } from '../services/usuarioService';
import MatriculaFormPage from './MatriculaFormPage';
import UsuarioFormPage from './UsuarioFormPage';

// ============================================================
// EstudiantesPage – Lista de estudiantes con acceso a matrícula
// ============================================================

type Vista = 'list' | 'new' | 'edit' | 'matricula';

const Ico = ({ d, cls = 'w-4 h-4' }: { d: string; cls?: string }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={cls}><path d={d} /></svg>
);
const D_PLUS   = 'M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z';
const D_SEARCH = 'M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z';
const D_BOOK   = 'M10.394 2.08a1 1 0 0 0-.788 0l-7 3a1 1 0 0 0 0 1.84L5.25 8.051a.999.999 0 0 1 .356-.257l4-1.714a1 1 0 1 1 .788 1.838L7.667 9.088l1.94.831a1 1 0 0 0 .787 0l7-3a1 1 0 0 0 0-1.838l-7-3Z';
const D_EDIT   = 'M2.695 14.763l-1.262 3.154a.5.5 0 0 0 .65.65l3.155-1.262a4 4 0 0 0 1.343-.885L17.5 5.5a2.121 2.121 0 0 0-3-3L3.58 13.42a4 4 0 0 0-.885 1.343Z';

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

// ── Componente principal ──────────────────────────────────────
const EstudiantesPage: React.FC = () => {
  const [vista, setVista]             = useState<Vista>('list');
  const [selectedUser, setSelectedUser] = useState<UsuarioListItem | null>(null);

  const [estudiantes, setEstudiantes] = useState<UsuarioListItem[]>([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState<string | null>(null);

  const [search, setSearch]             = useState('');
  const [activoFilter, setActivoFilter] = useState('');

  const loadEstudiantes = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params: { search?: string; activo?: boolean } = {};
      if (search)       params.search = search;
      if (activoFilter) params.activo = activoFilter === 'true';
      const data = await usuarioService.listarEstudiantes(params);
      setEstudiantes(data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar estudiantes');
    } finally { setLoading(false); }
  }, [search, activoFilter]);

  useEffect(() => { loadEstudiantes(); }, [loadEstudiantes]);

  const handleSuccess = () => { setVista('list'); loadEstudiantes(); };

  // ── Nuevo estudiante ──────────────────────────────────────
  if (vista === 'new') {
    return (
      <UsuarioFormPage
        rolFijo="ESTUDIANTE"
        onSuccess={handleSuccess}
        onCancel={() => setVista('list')}
      />
    );
  }

  // ── Editar estudiante ─────────────────────────────────────
  if (vista === 'edit' && selectedUser) {
    return (
      <UsuarioFormPage
        idUsuario={selectedUser.idUsuario}
        rolFijo="ESTUDIANTE"
        onSuccess={handleSuccess}
        onCancel={() => setVista('list')}
      />
    );
  }

  // ── Redirigir a matrícula ─────────────────────────────────
  if (vista === 'matricula' && selectedUser) {
    return (
      <MatriculaFormPage
        estudiante={selectedUser}
        onSuccess={handleSuccess}
        onCancel={() => setVista('list')}
      />
    );
  }

  return (
    <div className="space-y-5">

      {/* Cabecera */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Estudiantes</h1>
          <p className="text-slate-400 text-sm mt-0.5">
            {estudiantes.length} estudiante{estudiantes.length !== 1 ? 's' : ''} registrado{estudiantes.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-3 self-start sm:self-auto">
          <span className="inline-flex items-center gap-1.5 bg-green-100 text-green-700 text-xs font-bold
                           px-3 py-1.5 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
            Solo Estudiantes
          </span>
          <button onClick={() => setVista('new')}
            className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white
                       font-semibold text-sm px-4 py-2.5 rounded-xl transition-colors shadow-sm">
            <Ico d={D_PLUS} />
            Nuevo Estudiante
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              <Ico d={D_SEARCH} />
            </span>
            <input
              type="text"
              placeholder="Buscar por nombre, DNI o usuario..."
              value={search}
              onChange={e => { setSearch(e.target.value); }}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50
                         text-sm text-slate-800 placeholder-slate-300 outline-none
                         focus:ring-2 focus:ring-blue-500 transition-all"
            />
          </div>
          <select
            value={activoFilter}
            onChange={e => { setActivoFilter(e.target.value); }}
            className="px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm
                       text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 min-w-[130px]"
          >
            <option value="">Todos</option>
            <option value="true">Activos</option>
            <option value="false">Inactivos</option>
          </select>
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-16"><SpinnerIcon /></div>
        ) : error ? (
          <div className="text-center py-16 text-red-500 text-sm">{error}</div>
        ) : estudiantes.length === 0 ? (
          <div className="text-center py-16">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}
              className="w-12 h-12 text-slate-200 mx-auto mb-3">
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342"/>
            </svg>
            <p className="text-slate-400 text-sm font-medium">No se encontraron estudiantes</p>
            <p className="text-slate-300 text-xs mt-1">Cambia los filtros o regístralos desde Usuarios</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  {['Estudiante', 'DNI', 'Contacto', 'Estado', 'Último Acceso', 'Matrícula', 'Acciones'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-bold text-slate-400
                                           uppercase tracking-wider whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {estudiantes.map(u => (
                  <tr key={u.idUsuario} className="hover:bg-slate-50 transition-colors">

                    {/* Estudiante */}
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center
                                        bg-blue-100 text-blue-700 font-bold text-xs">
                          {u.nombres.charAt(0)}{u.apellidos.charAt(0)}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800 text-sm leading-tight">
                            {u.nombres} {u.apellidos}
                          </p>
                          <p className="text-slate-400 text-xs">@{u.username}</p>
                        </div>
                      </div>
                    </td>

                    {/* DNI */}
                    <td className="px-4 py-3.5 text-sm text-slate-600 whitespace-nowrap font-mono">
                      {u.dni}
                    </td>

                    {/* Contacto */}
                    <td className="px-4 py-3.5">
                      <p className="text-sm text-slate-600 leading-tight">{u.email}</p>
                      {u.telefono && (
                        <p className="text-xs text-slate-400 mt-0.5">{u.telefono}</p>
                      )}
                    </td>

                    {/* Estado */}
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full
                        ${u.activo ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${u.activo ? 'bg-green-500' : 'bg-slate-400'}`} />
                        {u.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>

                    {/* Último acceso */}
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      {u.ultimoAcceso ? (
                        <div>
                          <p className="text-xs text-slate-600">{u.ultimoAcceso.split(' ')[0]}</p>
                          <p className="text-xs text-slate-400">{u.ultimoAcceso.split(' ')[1]}</p>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-300">—</span>
                      )}
                    </td>

                    {/* Botón matrícula */}
                    <td className="px-4 py-3.5">
                      <button
                        onClick={() => { setSelectedUser(u); setVista('matricula'); }}
                        title="Gestionar matrícula"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold
                                   bg-green-50 text-green-700 hover:bg-green-100 border border-green-200
                                   transition-colors"
                      >
                        <Ico d={D_BOOK} cls="w-3.5 h-3.5" />
                        Matricular
                      </button>
                    </td>

                    {/* Botón editar */}
                    <td className="px-4 py-3.5">
                      <button
                        onClick={() => { setSelectedUser(u); setVista('edit'); }}
                        title="Editar estudiante"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold
                                   bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200
                                   transition-colors"
                      >
                        <Ico d={D_EDIT} cls="w-3.5 h-3.5" />
                        Editar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </div>
    </div>
  );
};

export default EstudiantesPage;
